import React, { useEffect } from "react";
import PropTypes from "prop-types";

import CampaignMedia from "./CampaignMedia";
import Footer from "../Footer";
import ReactShare from "shared/components/react-share/ReactShare";
import ProgressBar from "shared/components/progress-bar/ProgressBar";
import LiveCountdown from "shared/components/live-countdown/LiveCountdown";
import GivingButton from "shared/components/giving-form-v2/App/CampaignShowGivingButtonV2";
import GlobalModalWrapper from "shared/components/global-modal/GlobalModalWrapper";
import FormattedCurrencyNumber from "shared/components/react-intl/FormattedCurrencyNumber";
import styles from "./CampaignsPage.module.css";
import { setEntityCssVariables } from "shared/common-functions";
import SupporterScroll from "legacy/supporter-scroll";

// Note: Non modular CSS classes may be used by the support team for custom overrides, do not remove
const CampaignPage = ({
  campaign,
  entity,
  givingFormEntity,
  member,
  donorScrollItems,
  preview,
}) => {
  useEffect(() => {
    if (entity) {
      setEntityCssVariables(entity);
      new SupporterScroll();
    }
  }, [entity]);
  function languageToLocal(language) {
    const LANGUAGE_LOCALES = {
      English: "en",
      Cymraeg: "cy",
      French: "fr",
      German: "de",
      Italian: "it",
      Portuguese: "pt",
      Spanish: "es",
    };

    return LANGUAGE_LOCALES[language] || "en";
  }

  return (
    <div className={styles.campaignPageContainer}>
      {!preview && (
        <div
          className={styles.reactShareContainer}
          style={{
            borderImage: `linear-gradient(to bottom, rgba(${entity.primarycolor}), rgba(${entity.secondarycolor})) 1 100%`,
          }}>
          <h2 className={styles.shareTextSpan}>Share on</h2>
          <div className={styles.buttonsContainer}>
            <ReactShare
              campaignId={campaign.id}
              smsText={campaign.custom_sms_text || ""}
              twitterText={campaign.custom_twitter_text || ""}
            />
          </div>
        </div>
      )}
      <div className={`${styles.newCampaignPage} new-campaign-page`}>
        <div
          className={styles.campaignPageHeader}
          style={{ backgroundImage: `url(${campaign.banner?.url || campaign.banner})` }}>
          <div className={styles.campaignOverlay}></div>
          {campaign.show_countdown && (
            <LiveCountdown
              startDate={campaign.start_date}
              endDate={campaign.end_date}
              isOngoing={campaign.is_ongoing}
            />
          )}
        </div>
        <div className={`${styles.newCampaignCtaContainer} new-campaign-cta-container`}>
          <div className={styles.newCampaignHeaderContainer}>
            <div className={styles.inlineContainer}>
              <div className={styles.mainGoalContainer}>
                <div className={styles.inlineContainer}>
                  {campaign.show_logo && (campaign.logo?.url || campaign.logo) && (
                    <img
                      src={campaign.logo?.url || campaign.logo}
                      className={styles.newCampaignImg}
                      alt={campaign.logo_alt_text || campaign.name}
                    />
                  )}
                  {member && (
                    <>
                      {member.img ? (
                        <img
                          src={member.img?.url || member.img}
                          className={styles.profileImg}
                          alt="User Image"
                          width="95"
                          height="95"
                        />
                      ) : (
                        <img
                          src="no-user-image-square.jpg"
                          className={styles.profileImg}
                          alt="No User Image"
                          width="95"
                          height="95"
                        />
                      )}
                    </>
                  )}
                  {!campaign.hide_title && (
                    <h1
                      className={styles.newCampaignHeaderColor}
                      style={{
                        color: campaign.title_color || "#000000",
                      }}>
                      {campaign.name}
                    </h1>
                  )}
                </div>
                <div
                  className={`${styles.goalProgressContainer} goal-progress-container`}>
                  {campaign.show_dollars && (
                    <span
                      className={styles.totalRaisedNumber}
                      style={{
                        color: `rgb(${entity.primarycolor})`,
                      }}>
                      <FormattedCurrencyNumber
                        locale={languageToLocal(entity.language)}
                        value={Math.round(campaign.amount_raised_by_affinity || 0)}
                        currencyCode={entity.currency_code}
                      />
                    </span>
                  )}
                  {!campaign.hide_goal_bar &&
                    (campaign.status === "active" ||
                      campaign.status === "closed" ||
                      campaign.status === "staging") && (
                      <>
                        <ProgressBar
                          bgcolorPrimary={entity.primarycolor}
                          bgcolorSecondary={entity.secondarycolor}
                          completed={campaign.goal_progress_display}
                        />
                        <div className={`${styles.goal} goal`}>
                          <span className={styles.goalText}>Goal: &nbsp;</span>
                          <span className={`${styles.goalNumber} goal-number`}>
                            {campaign.show_currency_symbol ? (
                              <FormattedCurrencyNumber
                                locale={languageToLocal(entity.language)}
                                value={campaign.new_goal || 0}
                                currencyCode={entity.currency_code}
                              />
                            ) : (
                              campaign.new_goal.toLocaleString()
                            )}
                          </span>
                        </div>
                      </>
                    )}
                </div>
                <div className={styles.infoContainer}>
                  {campaign.show_supporters && (
                    <div className={`${styles.supporters} supporters`}>
                      {(campaign.status === "active" ||
                        campaign.status === "closed" ||
                        campaign.status === "staging") && (
                        <>
                          {campaign.camp_type === "donations" ||
                          campaign.type === "donations" ? (
                            <>
                              <span className={styles.number + " number"}>
                                {campaign.total_donations || 0}
                              </span>
                              <span className={styles.textSpan}>Gifts</span>
                            </>
                          ) : (
                            <>
                              <span className={styles.number + " number"}>
                                {campaign.total_supporters || 0}
                              </span>
                              <span className={styles.textSpan}>Supporters</span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {campaign.status === "active" && (
                    <GivingButton
                      cssClasses={`${styles.giftButton} ${styles.contribute} contribute gift-button primary-color-background btn btn-lg btn-block`}
                      buttonText="MAKE A GIFT"
                      externalGiveLink={entity.external_give_link}
                      campaign={campaign}
                      entityId={entity.id}
                    />
                  )}
                  <div className={styles.reactShareContainerMobile}>
                    <span className={styles.textSpan}>Share on</span>
                    <div className={styles.buttonsContainer}>
                      <ReactShare
                        campaignId={campaign.id}
                        smsText={campaign.custom_sms_text || ""}
                        twitterText={campaign.custom_twitter_text || ""}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.newCampaignMediaContainer}>
                <CampaignMedia campaign={campaign} />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.campaignDetails}>
          <div
            className={`${styles.newMediaAndSupporterInfo} new-media-and-supporter-info`}>
            <div className="row">
              <div className="col-md-8">
                <div className={`${styles.campaignTextContent} campaign-text-content`}>
                  {(campaign.descr || campaign.description) && (
                    <>
                      {campaign.descr_head && (
                        <h2
                          className={styles.primaryColorText}
                          style={{ fontSize: "18px" }}>
                          {campaign.descr_head}
                        </h2>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: campaign.descr || campaign.description,
                        }}
                      />
                    </>
                  )}
                  {campaign.how_donations_used && (
                    <>
                      {campaign.how_donations_used_head && (
                        <h2
                          className={styles.primaryColorText}
                          style={{ fontSize: "18px" }}>
                          {campaign.how_donations_used_head}
                        </h2>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: campaign.how_donations_used,
                        }}
                      />
                    </>
                  )}
                  {campaign.person_msg && (
                    <>
                      {campaign.person_msg_head && (
                        <h2
                          className={styles.primaryColorText}
                          style={{ fontSize: "18px" }}>
                          {campaign.person_msg_head}
                        </h2>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: campaign.person_msg,
                        }}
                      />
                    </>
                  )}
                  {campaign.why_donations && (
                    <>
                      {campaign.why_donations_head && (
                        <h2
                          className={styles.primaryColorText}
                          style={{ fontSize: "18px" }}>
                          {campaign.why_donations_head}
                        </h2>
                      )}
                      <p
                        dangerouslySetInnerHTML={{
                          __html: campaign.why_donations,
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className={`${styles.campaignTextContent} campaign-text-content`}>
                  <div
                    className={styles.gradientLine}
                    style={{
                      background: `linear-gradient(to left, rgba(${entity.primarycolor}, 1), rgba(${entity.secondarycolor}, 1))`,
                    }}
                  />
                  {(campaign.status === "active" ||
                    campaign.status === "closed" ||
                    campaign.status === "staging") && (
                    <>
                      <h3 className={styles.thanks}>Thanks To Our Recent Donors!</h3>
                      <div
                        className={`${styles.supporterMessagesContainer} supporter-messages-container`}>
                        <ul className={`${styles.fbMessages} fb-messages`}>
                          {(donorScrollItems || []).map((d, index) => (
                            <li key={index} className={`${styles.fbMessage} fb-message`}>
                              <div>
                                {d.post_id && (d.public || d.pub) && (
                                  <img
                                    className={styles.profileImg}
                                    src={`//graph.facebook.com/${
                                      d.post_id.split("_")[0]
                                    }/picture`}
                                    alt=""
                                  />
                                )}
                                <div className={styles.fbUserInfo}>
                                  <div className={`${styles.fbUserName} fb-user-name`}>
                                    {d.full_name_with_default}
                                  </div>

                                  {entity.display_donation_amount && !d.hide_amount && (
                                    <p
                                      className={`${styles.donationAmount} donation-amount`}>
                                      - {d.gift_aid_amount}
                                    </p>
                                  )}
                                </div>
                                <p className={styles.msgContent}>{d.fb_message}</p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={styles.buttonContainerPausePlay}>
                        <button
                          data-testid="t-campaig--Campaig-pause-scrolling-mess"
                          className={`pause-button primary-color-background ${styles.giftButton} btn`}
                          type="button"
                          aria-label="Pause scrolling messages">
                          Pause
                        </button>
                        <button
                          data-testid="t-campaig--Campaig-play-scrolling-messa"
                          className={`play-button primary-color-background ${styles.giftButton} btn`}
                          type="button"
                          aria-label="Play scrolling messages"
                          style={{ display: "none" }}>
                          Play
                        </button>
                      </div>
                    </>
                  )}
                  {campaign.status === "active" && (
                    <GivingButton
                      cssClasses={`${styles.giftButton} ${styles.contribute} contribute gift-button primary-color-background btn btn-lg btn-block`}
                      buttonText={
                        campaign.call_to_action_button_text || "BE PART OF THIS LIST"
                      }
                      externalGiveLink={entity.external_give_link}
                      campaign={campaign}
                      entityId={entity.id}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!preview && (
        <>
          <Footer
            entity={entity}
            entityUrls={entity.entity_urls}
            campaignName={campaign.name}
          />

          <GlobalModalWrapper entity={givingFormEntity} entityId={entity.id} />
        </>
      )}
    </div>
  );
};

CampaignPage.propTypes = {
  campaign: PropTypes.object.isRequired,
  entity: PropTypes.object.isRequired,
  givingFormEntity: PropTypes.object,
  member: PropTypes.object,
  donorScrollItems: PropTypes.array,
  preview: PropTypes.bool,
  menuBar: PropTypes.object,
};

export default CampaignPage;
